import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, MapPin, Plus, Trash2, X, Loader2, Home, Briefcase, Building, Check, Pencil } from "lucide-react";
import axios from "axios";
import LocationPicker from "../components/LocationPicker";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const AddressesPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocationForForm, setSelectedLocationForForm] = useState(null);
  const [formData, setFormData] = useState({
    label: "",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    pincode: "",
    lat: null,
    lng: null
  });
  const [error, setError] = useState("");

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchAddresses();
    }
  }, [user]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses/${user.id}`);
      setAddresses(response.data);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "pincode") {
      const cleaned = value.replace(/\D/g, "").slice(0, 6);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationSelect = (location) => {
    const pincodeMatch = location.address.match(/\b\d{6}\b/);
    const pincode = pincodeMatch ? pincodeMatch[0] : "";
    
    // Check pincode serviceability
    if (pincode && pincode !== "560075") {
      setError("Sorry, we are not serving in this area (pincode: " + pincode + ")");
      setShowLocationPicker(false);
      return;
    }
    
    setSelectedLocationForForm(location);
    setFormData(prev => ({
      ...prev,
      lat: location.lat,
      lng: location.lng,
      pincode: pincode || prev.pincode
    }));
    setShowLocationPicker(false);
    setError("");
  };

  const resetForm = () => {
    setFormData({
      label: "",
      address_line1: "",
      address_line2: "",
      address_line3: "",
      pincode: "",
      lat: null,
      lng: null
    });
    setEditingAddress(null);
    setShowAddForm(false);
    setSelectedLocationForForm(null);
    setError("");
  };

  const openEditForm = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label,
      address_line1: address.address_line1,
      address_line2: address.address_line2 || "",
      address_line3: address.address_line3 || "",
      pincode: address.pincode,
      lat: address.lat || null,
      lng: address.lng || null
    });
    setSelectedLocationForForm(address.lat ? { lat: address.lat, lng: address.lng, address: address.address_line1 } : null);
    setShowAddForm(true);
  };

  const handleSaveAddress = async () => {
    if (!formData.label.trim()) {
      setError("Please enter a label");
      return;
    }
    if (!formData.address_line1.trim()) {
      setError("Please enter address line 1");
      return;
    }
    if (!formData.pincode || formData.pincode.length !== 6) {
      setError("Please enter a valid 6-digit pincode");
      return;
    }
    
    // Check if pincode is serviceable
    if (formData.pincode !== "560075") {
      alert("Sorry, we are not serving in this area");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      if (editingAddress) {
        // Update existing address
        await axios.put(`${API}/addresses/${editingAddress.id}`, {
          ...formData,
          user_id: user.id
        });
      } else {
        // Create new address
        await axios.post(`${API}/addresses`, {
          user_id: user.id,
          ...formData,
          is_default: addresses.length === 0
        });
      }
      
      fetchAddresses();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save address");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    const addressToDelete = addresses.find(a => a.id === addressId);
    if (addressToDelete?.is_default) {
      alert("Cannot delete default address. Please set another address as default first.");
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this address?")) return;

    try {
      await axios.delete(`${API}/addresses/${addressId}`);
      setAddresses(prev => prev.filter(a => a.id !== addressId));
    } catch (error) {
      console.error("Error deleting address:", error);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await axios.put(`${API}/addresses/${addressId}/default`, {
        user_id: user.id
      });
      
      setAddresses(prev => prev.map(addr => ({
        ...addr,
        is_default: addr.id === addressId
      })));
    } catch (error) {
      console.error("Error setting default address:", error);
    }
  };

  const getLabelIcon = (label) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes("home")) return Home;
    if (lowerLabel.includes("office") || lowerLabel.includes("work")) return Briefcase;
    return Building;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  const hasDefaultAddress = addresses.some(a => a.is_default);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="addresses-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate("/home")}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">My Addresses</h1>
          <Button
            onClick={() => { resetForm(); setShowAddForm(true); }}
            className="ml-auto bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
            data-testid="add-address-btn"
          >
            <Plus size={18} className="mr-1" />
            Add New
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Primary Address (from profile) */}
        <div 
          className={`bg-white rounded-xl shadow-sm p-4 mb-4 ${!hasDefaultAddress ? 'border-2 border-[#3FA9F5]' : 'border border-gray-100'}`} 
          data-testid="primary-address"
        >
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 ${!hasDefaultAddress ? 'bg-[#3FA9F5]/10' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
              <Home size={20} className={!hasDefaultAddress ? 'text-[#3FA9F5]' : 'text-gray-500'} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{user.label || "Home"}</h3>
                {!hasDefaultAddress && (
                  <span className="text-xs bg-[#3FA9F5] text-white px-2 py-0.5 rounded-full">Default</span>
                )}
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Profile</span>
              </div>
              <p className="text-gray-600 text-sm mt-1">
                {user.address_line1}, {user.address_line2}, {user.address_line3}
              </p>
              <p className="text-gray-500 text-sm">{user.pincode}</p>
            </div>
            <button 
              onClick={() => navigate("/profile")}
              className="p-2 text-[#3FA9F5] hover:bg-[#3FA9F5]/10 rounded-full"
              data-testid="edit-profile-address"
            >
              <Pencil size={18} />
            </button>
          </div>
        </div>

        {/* Other Addresses */}
        {addresses.length > 0 && (
          <div className="space-y-4">
            {addresses.map((address) => {
              const IconComponent = getLabelIcon(address.label);
              return (
                <div 
                  key={address.id} 
                  className={`bg-white rounded-xl shadow-sm p-4 ${address.is_default ? 'border-2 border-[#3FA9F5]' : 'border border-gray-100'}`}
                  data-testid={`address-${address.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 ${address.is_default ? 'bg-[#3FA9F5]/10' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                      <IconComponent size={20} className={address.is_default ? 'text-[#3FA9F5]' : 'text-gray-500'} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{address.label}</h3>
                        {address.is_default && (
                          <span className="text-xs bg-[#3FA9F5] text-white px-2 py-0.5 rounded-full">Default</span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mt-1">
                        {address.address_line1}
                        {address.address_line2 && `, ${address.address_line2}`}
                        {address.address_line3 && `, ${address.address_line3}`}
                      </p>
                      <p className="text-gray-500 text-sm">{address.pincode}</p>
                      
                      {/* Set as Default Button */}
                      {!address.is_default && (
                        <button 
                          onClick={() => handleSetDefault(address.id)}
                          className="mt-2 text-sm text-[#3FA9F5] hover:underline flex items-center gap-1"
                          data-testid={`set-default-${address.id}`}
                        >
                          <Check size={14} />
                          Set as default
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button 
                        onClick={() => openEditForm(address)}
                        className="p-2 text-[#3FA9F5] hover:bg-[#3FA9F5]/10 rounded-full"
                        data-testid={`edit-address-${address.id}`}
                      >
                        <Pencil size={18} />
                      </button>
                      <button 
                        onClick={() => handleDeleteAddress(address.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                        data-testid={`delete-address-${address.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {addresses.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <MapPin size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No additional addresses saved</p>
          </div>
        )}
      </div>

      {/* Add/Edit Address Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingAddress ? "Edit Address" : "Add New Address"}
                </h2>
                <button 
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Location Picker Button */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Location on Map <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowLocationPicker(true)}
                    className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-[#3FA9F5] hover:bg-[#3FA9F5]/5 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center">
                        <MapPin size={20} className="text-[#3FA9F5]" />
                      </div>
                      <div className="flex-1">
                        {selectedLocationForForm ? (
                          <>
                            <p className="text-sm font-medium text-green-600">✓ Location Selected</p>
                            <p className="text-xs text-gray-500 line-clamp-2">{selectedLocationForForm.address}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-[#3FA9F5]">Tap to Select Location</p>
                            <p className="text-xs text-gray-500">Search or pin your delivery location</p>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Label <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {["Home", "Office", "Other"].map((label) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, label }))}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          formData.label === label 
                            ? 'bg-[#3FA9F5] text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flat / House No / Building <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="address_line1"
                    placeholder="e.g., Flat 101, Tower A"
                    value={formData.address_line1}
                    onChange={handleChange}
                    className="h-12 rounded-xl"
                    data-testid="form-address1-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street / Landmark</label>
                  <Input
                    type="text"
                    name="address_line2"
                    placeholder="e.g., Near Park, Main Road"
                    value={formData.address_line2}
                    onChange={handleChange}
                    className="h-12 rounded-xl"
                    data-testid="form-address2-input"
                  />
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                <Button
                  onClick={handleSaveAddress}
                  disabled={isSaving || !selectedLocationForForm}
                  className="w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white rounded-xl mt-4 disabled:opacity-50"
                  data-testid="save-address-btn"
                >
                  {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingAddress ? "Update Address" : "Save Address")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPicker
          onLocationSelect={handleLocationSelect}
          onClose={() => setShowLocationPicker(false)}
          initialLocation={selectedLocationForForm}
        />
      )}
    </div>
  );
};

export default AddressesPage;
